
    import {Inject, Options, Vue, Prop} from 'vue-property-decorator';
    import ProgressTrackerV2 from '@/brief-hub/deposit/progress-tracker/ProgressTrackerV2.vue';
    import {Getter} from 'vuex-class';
    import {ActionsType} from '@/brief-hub/store/modules/brief/actions';
    import {DI} from '@/brief-hub/config/DependencyInjection';
    import {NavigationService} from '@/brief-hub/brief/NavigationService';
    import BriefResource from '@/brief-hub/brief/BriefResource';
    import {RouteNames} from '@/brief-hub/router/route-names';
    import {MutationsType} from '../../../store/modules/brief/mutations';
    import {HttpServiceResponse} from 'vue3-starter/src/typescript/http/http';
    import UserResource from '@/brief-hub/user/UserResource';
    import {DraftStateWrapper} from '@/common/api/api-raw-types';
    import SelectedFreelancerList from '@/project-proposal/common/ui/SelectedFreelancerList.vue';
    import {Breakpoints} from '@/common/plugins/vue-mq-breakpoints';
    import {pushVJoySnackbar} from '@maltjoy/core-vue';

    @Options({
        components: {
            SelectedFreelancerList,
            ProgressTrackerV2,
        },
    })
    export default class BriefHubStepLayoutV2 extends Vue {
        @Inject({from: DI.navigationService})
        navigationService!: NavigationService;

        @Inject({from: 'mq'})
        mq!: {current: Breakpoints};

        @Prop({type: Boolean, default: true})
        hasFooter!: boolean;

        @Getter('isFormValid')
        isFormValid!: boolean;

        @Getter('brief')
        brief!: BriefResource | null;

        @Getter('saved')
        saved!: boolean;

        @Getter('originReferrer')
        originReferrer!: string | null;

        @Getter('connectedUser')
        user!: UserResource | null;

        private loading = false;

        created(): void {
            document.title = this.$msg('customer.brief.form.page.title');
            if (this.saved) {
                setTimeout(() => this.$store.commit(MutationsType.SAVED_ACKNOWLEDGEMENT), 5000);
            }
        }

        get isMobile(): boolean {
            return ['tiny', 'xxs', 'xs', 'sm'].includes(this.mq.current);
        }

        get stringifiedRouteName(): string | null {
            if (!this.$route.name) {
                return null;
            }
            return typeof this.$route.name === 'symbol' ? this.$route.name.toString() : this.$route.name;
        }

        get briefComplete(): boolean {
            const draftStateWrapper = this.brief?.draftState as DraftStateWrapper;
            return this.brief ? draftStateWrapper?.draftState === 'MISSION_DESCRIPTION_SECTION_COMPLETED' : false;
        }

        get previousAvailable(): boolean {
            // for the unlogged funnel we prevent the user to go back to the brief home screen
            return !!this.previousRoute && !!this.user;
        }

        get showNext(): boolean {
            return !this.briefComplete || this.$route.name !== RouteNames.BRIEF_MISSION_DESCRIPTION;
        }

        get previousRoute(): string | null {
            if (!this.brief || !this.stringifiedRouteName) {
                return null;
            }
            return this.navigationService.previousRoute(this.stringifiedRouteName);
        }

        get isUnloggedOrStandardUser(): boolean {
            return !this.user || this.user?.serviceLevelType === 'STANDARD';
        }

        upgradeServiceLevel(): void {
            const pageSource = 'blocker';
            const sourceComponent = 'brief_top_bar_upgrade_button';
            let redirectUrl = `/customer/searches${this.$route.path}`;
            redirectUrl += '?referrer=' + document.referrer;
            window.location.href = `/client-settings/service-level?pageSource=${pageSource}&sourceComponent=${sourceComponent}&successRedirectPath=${redirectUrl}&dropRedirectPath=${redirectUrl}`;
        }

        goToPreviousStep(): void {
            const previousRoute = this.previousRoute || RouteNames.NOT_FOUND;
            this.$router.push({name: previousRoute, params: {id: this.brief?.briefId ?? ''}});
        }

        async saveBrief(): Promise<HttpServiceResponse<BriefResource>> {
            this.loading = true;
            const [data, error] = await this.$store.dispatch(ActionsType.SAVE_BRIEF);
            this.loading = false;
            if (!data || error) {
                pushVJoySnackbar({
                    props: {
                        message: this.$msg('customer.brief.error.unknown'),
                        level: 'error',
                    },
                });
            }
            return [data, error];
        }

        async onFormSubmit(event: Event): Promise<void> {
            if (!this.isFormValid) {
                return;
            }
            const [, error] = await this.saveBrief();
            if (!error && this.brief?.briefId) {
                const next = this.navigationService.nextIncompleteRoute(this.brief.draftState as DraftStateWrapper);
                const nextLogical = this.navigationService.nextRoute(this.stringifiedRouteName!);
                this.$router.push({
                    name: this.$route.name === RouteNames.BRIEF_NEW ? next : nextLogical,
                    params: {id: this.brief.briefId},
                });
            }
        }

        async backToOverview(): Promise<void> {
            const [, error] = await this.saveBrief();
            if (!error && this.brief) {
                this.$router.push({
                    name: RouteNames.BRIEF_OVERVIEW,
                    params: {
                        id: this.brief.briefId,
                        link: this.stringifiedRouteName || '',
                    },
                });
            }
        }

        goToReferrer(): void {
            if (this.$route.query.referrer) {
                window.location.href = this.$route.query.referrer as string;
            } else if (this.originReferrer) {
                window.location.href = this.originReferrer;
            } else {
                // Have to refresh page to prevent funnel mode, so use window.location instead of $router.push().
                window.location.href = '/project-proposal-summaries';
            }
        }
    }
